"use client";

import BaseModal from "../common/BaseModal";
import TextInput from "../input/TextInput";
import {useContext, useState, useCallback, useEffect} from "react";
import DefaultButton from "../buttons/DefaultButton";
import {useAuth} from "../../providers/authProvider";
import {ApiContext} from "../contexts/ApiContext";
import SelectInput from "../input/SelectInput";

function EditQuestionModal(
    props: {
        openModal: boolean,
        setOpenModal: (arg: boolean) => void,
        CompanyVisitID: number,
        ChildQuestion?: boolean,
        existingQuestion: any,
        onUpdate?: () => void,
    }){
    const {token} = useAuth();
    const {Url} = useContext(ApiContext);
    const {setOpenModal, ChildQuestion, CompanyVisitID, existingQuestion, onUpdate} = props;

    const [question, setQuestion] = useState('');
    const [type, setType] = useState('');
    const [options, setOptions] = useState<any[]>([]);
    const [tempOption, setTempOption] = useState<any>('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (existingQuestion) {
            setQuestion(existingQuestion.Question || '');
            setType(existingQuestion.Type || '');
            setOptions(existingQuestion.Options || []);
        }
    }, [existingQuestion]);
    let apiUrl: string = Url + 'admin/questions'
    if (ChildQuestion){
        apiUrl = Url + 'admin/child-questions'
    }
    const resetForm = useCallback(() => {
        setQuestion('');
        setType('');
        setOptions([]);
        setTempOption('');
        setMessage('');
        setOpenModal(false);
    }, [setOpenModal]);

    const editQuestion = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        try {

            const response = await fetch(apiUrl, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ID: existingQuestion.ID,
                    Question: question,
                    Type: type,
                    CompanyVisitID: CompanyVisitID,
                    Options: options,
                }),
            });

            if (response.ok) {
                if (onUpdate) onUpdate();
                resetForm();
            } else {
                const data = await response.json();
                setMessage(data.message || 'Er is een fout opgetreden.');
            }
        } catch {
            setMessage('Er is een netwerkfout opgetreden.');
        }
    }, [apiUrl, token, question, type, CompanyVisitID, options, existingQuestion, onUpdate, resetForm]);

    const deleteQuestion = useCallback(async () => {
        try {
            const response = await fetch(`${apiUrl}/${existingQuestion.ID}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                if (onUpdate) onUpdate();
                resetForm();
            } else {
                const data = await response.json();
                setMessage(data.message || 'Er is een fout opgetreden.');
            }
        } catch {
            setMessage('Er is een netwerkfout opgetreden.');
        }
    }, [apiUrl, token, existingQuestion, onUpdate, resetForm]);

    let optionsLayout;

    if (type === "select") {
        optionsLayout = (
            <div className="mt-4">
                <TextInput
                    labelName="Voeg een optie toe"
                    inputName="tempOption"
                    onChange={(e) => setTempOption(e.target.value)}
                    value={tempOption}
                    placeholder="Typ hier een optie"
                    errorMessage="De optie mag niet leeg zijn"
                    type="text"
                />

                <DefaultButton
                    onClick={() => {
                        if (tempOption.trim()) {
                            setOptions((prevOptions) => [...prevOptions, tempOption]);
                            setTempOption('');
                        }
                    }}
                    content="Optie toevoegen"
                />

                {options.length > 0 && (
                    <div className="mt-4">
                        <p className="text-gray-700 font-medium">Toegevoegde opties:</p>
                        <ul className="mt-2 bg-gray-50 border border-gray-200 p-4 rounded-md">
                            {options.map((option, index) => (
                                <li
                                    key={index}
                                    className="flex justify-between items-center bg-gray-100 p-2 rounded-md mb-2 shadow-sm">
                                    <span className="text-gray-800">{option}</span>
                                    <button
                                        className="ml-4 text-red-500 hover:text-red-700"
                                        onClick={() => {
                                            setOptions((prevOptions) =>
                                                prevOptions.filter((_, i) => i !== index)
                                            );
                                        }}
                                    >
                                        &#x2716;
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    return (
        <BaseModal
            show={props.openModal}
            onClose={resetForm}
            title="Vraag bewerken"
            footer={
                <>
                    <DefaultButton onClick={editQuestion} content="Opslaan"/>
                    <DefaultButton onClick={resetForm} content="Annuleren"/>
                    <DefaultButton onClick={deleteQuestion} content="Verwijder"/>
                </>
            }
        >
            <p className="text-base leading-relaxed text-gray-500">
                Pas de vraag en eventueel de selectie opties aan.
            </p>
            <p className="text-base leading-relaxed text-red-500">
                {message}
            </p>
            <TextInput
                labelName="Vraag"
                inputName="question"
                onChange={(e) => setQuestion(e.target.value)}
                value={question}
                placeholder="Naam"
                errorMessage="Vul hier de vraag in"
                type="text"
            />
            <SelectInput
                labelName={"Soort vraag"}
                inputName={"type"}
                value={type}
                options={
                    [
                        {
                            ID: "text",
                            Name: "Tekst"
                        },
                        {
                            ID: "select",
                            Name: "Selectie (Poll)"
                        },
                        {
                            ID: "checkbox",
                            Name: "Checkbox (Ja/Nee)"
                        },
                    ]
                }
                errorMessage={"Niks ingevuld"}
                onChange={(e) => setType(e.target.value)}/>
            {optionsLayout}
        </BaseModal>
    );
}

export default EditQuestionModal;
